import { Text } from '@bonusly/design-system';

import { PaginationParams } from '../../types/pagination';
import { PaginationButton } from './pagination-button';

/*
  This pagination component is based on the following design:
  - The first and last page numbers are always shown
  - The current page number is always shown
  - The numbers surrounding the current page number are shown
  - The numbers in between the first and last numbers and the surrounding numbers are replaced with an ellipsis
  - If there are 5 or less pages, all numbers are shown
  - A maximum of 7 characters will display at a time with a maximum of 5 numbers and 2 ellipses
*/

// Replace all numbers that are not to be shown with undefined
export const filteredPageNumbers = (data: PaginationParams) => {
	const pageNumbers: (number | undefined)[] = Array.from({ length: data.page_count || 1 }, (_, i) => i + 1);

	if (!data.page_number || !data.page_count || data.page_count <= 5) return pageNumbers;

	const currentIndex = data.page_number - 1;
	const secondToLastPage = data.page_count - 1;
	const firstSet = pageNumbers.slice(0, 3);
	const lastSet = pageNumbers.slice(pageNumbers.length - 3, pageNumbers.length);

	if (firstSet.includes(data.page_number)) {
		for (let i = 4; i < secondToLastPage; i++) pageNumbers[i] = undefined;
	} else if (lastSet.includes(data.page_number)) {
		const maxValue = currentIndex - lastSet.findIndex((value) => value === data.page_number) - 1;
		for (let i = 1; i < maxValue; i++) pageNumbers[i] = undefined;
	} else {
		for (let i = 1; i < currentIndex - 1; i++) pageNumbers[i] = undefined;
		if (data.page_next) {
			for (let i = data.page_next; i < secondToLastPage; i++) pageNumbers[i] = undefined;
		}
	}
	return pageNumbers;
};

// Turn undefined values into one ellipsis per set of undefined values
export const pageNumberFormatter = (
	handleSelection: (event: { currentTarget: { id: any } }) => void,
	pageNumbers: (number | undefined)[],
	currentPage?: number
) => {
	const values = pageNumbers.map((value, i) =>
		value ? <PaginationButton currentPage={currentPage} handleSelection={handleSelection} key={value} pageNumber={i + 1} /> : undefined
	);

	const formattedArray: any[] = [];
	let lastWasEllipsis = false;
	for (let i = 0; i < values.length; i++) {
		if (values[i]) {
			formattedArray.push(values[i]);
			lastWasEllipsis = false;
		} else if (!lastWasEllipsis) {
			formattedArray.push(<Text key={`ellipsis-${i}`}>...</Text>);
			lastWasEllipsis = true;
		}
	}
	return formattedArray;
};

export const PaginationButtons = (data: PaginationParams, handleSelection: (event: { currentTarget: { id: any } }) => void) => {
	const pageNumbers = filteredPageNumbers(data);
	return pageNumberFormatter(handleSelection, pageNumbers, data.page_number);
};
